import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { errorToast, successToast } from "../../components/Toast";
import QualitySpecificationsService from "../../services/_qualitySpecifications";
import { getParentProductNameListApi } from "../../services/_product";
import ReactSelect from "react-select";

interface Product {
  id: string;
  name: string;
}

interface QualitySpec {
  name: string;
  description?: string | null;
}
const CreateQualitySpecifications = () => {
  const navigate = useNavigate();

  const [productList, setProductList] = useState<Product[]>([]);
  const [allQualitySpecs, setAllQualitySpecs] = useState<QualitySpec[]>([]);
  const [filterProduct, setFilterProduct] = useState<string>("");
  const [filterSpecs, setFilterSpecs] = useState<QualitySpec[]>([]);
  const [createSpecs, setCreateSpecs] = useState<QualitySpec[]>([]);
  const [createProduct, setCreateProduct] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    createProduct?: string;
    specs?: string[];
  }>({
    createProduct: "",
    specs: [],
  });

  // Load products
  const getProducts = async () => {
    try {
      const res: any = await getParentProductNameListApi("status=true");
      setProductList(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Load all quality specifications
  const getQualitySpecifications = async () => {
    try {
      const res: any =
        await QualitySpecificationsService.getAllQualitySpecificationsApi();

      // 🔥 FIX: add label & value so ReactSelect returns full object
      const formatted = res.data.map((s: any) => ({
        ...s,
        label: s.name,
        value: s.name,
      }));

      setAllQualitySpecs(formatted);
      setFilterSpecs(formatted);
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    getProducts();
    getQualitySpecifications();
  }, []);

  // Filter specs when product filter changes
  useEffect(() => {
    if (filterProduct) {
      const filtered = allQualitySpecs.filter(
        (spec: any) => spec.product_id === filterProduct,
      );
      setFilterSpecs(filtered);
    } else {
      setFilterSpecs(allQualitySpecs);
    }
  }, [filterProduct, allQualitySpecs]);

  // Add selected spec to create section
  const addSpecToCreate = (spec: QualitySpec) => {
    if (!createSpecs.find((s) => s.name.trim() === spec.name.trim())) {
      setCreateSpecs((prev) => [
        ...prev,
        {
          name: spec.name,
          description: spec.description ? spec.description : "", // ⭐ prevent null
        },
      ]);
    } else {
      errorToast(`"${spec.name}" already added`);
    }
  };

  // Add all specs in filter section to create section
  const addAllSpecs = () => {
    const newSpecs = filterSpecs
      .filter(
        (spec) => !createSpecs.find((s) => s.name.trim() === spec.name.trim()),
      )
      .map((s) => ({ name: s.name, description: s.description || "" }));
    if (newSpecs.length === 0) return errorToast("All specs already added");
    setCreateSpecs([...createSpecs, ...newSpecs]);
  };

  // Add empty new spec
  const addNewSpec = () => {
    setCreateSpecs([...createSpecs, { name: "", description: "" }]);
  };

  // Remove spec from create section
  const removeSpec = (index: number) => {
    const newList = [...createSpecs];
    newList.splice(index, 1);
    setCreateSpecs(newList);
  };

  // Handle input change
  const handleSpecChange = (
    index: number,
    field: "name" | "description",
    value: string,
  ) => {
    const newList = [...createSpecs];
    newList[index][field] = value;
    setCreateSpecs(newList);

    if (field === "name") {
      const newErrors = { ...errors };
      if (!value.trim()) {
        newErrors.specs![index] = "Please provide a name";
      } else {
        if (newErrors.specs) newErrors.specs[index] = "";
      }
      setErrors(newErrors);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    let hasError = false;
    const newErrors: { createProduct?: string; specs: string[] } = {
      specs: [],
    };

    if (!createProduct) {
      newErrors.createProduct = "Please select a product";
      hasError = true;
    }

    createSpecs.forEach((spec, index) => {
      if (!spec.name.trim()) {
        newErrors.specs![index] = "Please provide a name";
        hasError = true;
      } else {
        newErrors.specs![index] = "";
      }
    });

    setErrors(newErrors);

    if (hasError) return;
    if (createSpecs.length === 0)
      return errorToast("Please add at least one quality specification");

    // Validate required name and duplicates
    const names = createSpecs.map((s) => s.name.trim());
    if (names.includes(""))
      return errorToast("Name is required for all specifications");
    const duplicates = names.filter((n, i) => names.indexOf(n) !== i);
    if (duplicates.length > 0)
      return errorToast(
        `Duplicate names: ${Array.from(new Set(duplicates)).join(", ")}`,
      );

    const payload = {
      product_id: createProduct,
      qualitySpecifications: createSpecs.map((s) => ({
        name: s.name.trim(),
        description: (s.description || "").trim(),
      })),
    };

    setLoading(true);
    try {
      const res: any =
        await QualitySpecificationsService.createManyQualitySpecificationsApi(
          payload,
        );
      if (res.isSuccess) {
        successToast(res.message);
        navigate("/quality-specification-list");
      } else {
        errorToast(res.message);
      }
    } catch (err: any) {
      errorToast(err.message || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <div className="card">
      <div className="card-header pb-0">
        <h5>Create Quality Specifications</h5>
      </div>
      <div className="card-body">
        {/* Filter Section */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-4">
            <label className="form-label" style={{ fontSize: "0.9375rem" }}>
              Product (Filter)
            </label>
            <ReactSelect
              options={productList}
              getOptionLabel={(option: any) => option.name}
              getOptionValue={(option: any) => option.id}
              placeholder="Select Product"
              isClearable
              onChange={(option: any) =>
                setFilterProduct(option ? option.id : "")
              }
            />
          </div>

          <div className="col-12 col-lg-8 d-flex align-items-end">
            <div className="flex-grow-1 me-2">
              <label className="form-label" style={{ fontSize: "0.9375rem" }}>
                Quality Specification (Filter) :
              </label>
              <ReactSelect
                options={filterSpecs}
                getOptionLabel={(option: any) => option.name}
                getOptionValue={(option: any) => option.name}
                placeholder="Select Quality Specification"
                isClearable
                onChange={(option: any) => option && addSpecToCreate(option)}
              />
            </div>

            {filterProduct && (
              <button
                type="button"
                className="btn btn-outline-primary ms-2"
                style={{
                  height: "38px",
                  alignSelf: "flex-end",
                  whiteSpace: "nowrap",
                  padding: "0 12px",
                }}
                onClick={addAllSpecs}
              >
                Select All
              </button>
            )}
          </div>
        </div>

        <hr style={{ marginBottom: 20, marginTop: 30 }} />

        {/* Create Section */}
        <div className="border p-3 mb-4" style={{ borderRadius: "10px" }}>
          <div className="row g-3 mb-3 align-items-center">
            <div className="col-12 col-lg-4 d-flex align-items-start">
              <div className="flex-grow-1 me-2">
                <label className="form-label" style={{ fontSize: "0.9375rem" }}>
                  Product (Create):<span className="text-danger">*</span>
                </label>
                <ReactSelect
                  options={productList}
                  getOptionLabel={(option: any) => option.name}
                  getOptionValue={(option: any) => option.id}
                  placeholder="Select Product"
                  isClearable
                  onChange={(option: any) => {
                    setCreateProduct(option ? option.id : "");
                    setErrors((prev) => ({ ...prev, createProduct: "" }));
                  }}
                />
                {errors.createProduct && (
                  <div className="text-danger mt-1 small">
                    {errors.createProduct}
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center mt-5">
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={addNewSpec}
                  style={{ whiteSpace: "nowrap", marginLeft: 20 }}
                >
                  + Add New
                </button>
              </div>
            </div>
          </div>

          {/* Specs Div List */}
          <div className="row g-3">
            {createSpecs.map((spec, index) => (
              <div
                key={index}
                className="col-12 border p-3 position-relative mb-2"
                style={{ borderRadius: "10px" }}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-danger position-absolute top-0 end-0"
                  style={{ borderRadius: "5px" }}
                  onClick={() => removeSpec(index)}
                >
                  X
                </button>
                <div className="row g-2">
                  <div className="col-6">
                    <label>
                      Name: <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={spec.name}
                      onChange={(e) =>
                        handleSpecChange(index, "name", e.target.value)
                      }
                    />
                    {errors.specs && errors.specs[index] && (
                      <div className="text-danger mt-1 small">
                        {errors.specs[index]}
                      </div>
                    )}
                  </div>
                  <div className="col-6">
                    <label>Description :</label>
                    <input
                      type="text"
                      className="form-control"
                      value={spec.description ?? ""}
                      onChange={(e) =>
                        handleSpecChange(index, "description", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-3">
          <button
            type="button"
            className="btn btn-primary me-3"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading && (
              <span className="spinner-border spinner-border-sm me-2"></span>
            )}
            Submit
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/quality-specification-list")}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateQualitySpecifications;
